/**
 * CleanersDemoAccountsCard — Iter-111 · Fix 3.
 * Admin-only (CEO / Directors) grid of the 9 self-contained Cleaners CC
 * commercial demo accounts + one-click (re)generate. Mounted on the
 * Cleaners Command Center page.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, ExternalLink } from 'lucide-react';
import { Card } from '../ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const CleanersDemoAccountsCard = () => {
  const [accounts, setAccounts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = () => {
    fetch(`${API}/api/cleaners/demo-accounts`, { credentials: 'include' })
      .then((r) => {
        if (r.status === 403) { setDenied(true); return null; }
        return r.ok ? r.json() : null;
      })
      .then((d) => { if (d) setAccounts(d.accounts || []); })
      .catch(() => {});
  };
  useEffect(load, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/cleaners/demo-accounts/generate`, { method: 'POST', credentials: 'include' });
      if (!r.ok) throw new Error('Generation failed');
      const d = await r.json();
      toast.success(`${d.count} Cleaners demo accounts ready`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (denied || accounts === null) return null;

  return (
    <Card className="bg-zinc-900 border-zinc-700 p-5 mb-4" data-testid="cleaners-demo-accounts-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <p className="text-xs uppercase tracking-widest font-bold" style={{ color: '#22d3ee' }}>
          Cleaners Commercial Demo Accounts · 9 Types · Internal Review
        </p>
        <button type="button" onClick={generate} disabled={busy}
                className="text-[10px] uppercase tracking-widest font-black px-3 py-1.5 rounded-full flex items-center gap-1.5"
                style={{ backgroundColor: '#22d3ee', color: '#0a0a0a' }}
                data-testid="cleaners-demo-generate-btn">
          <Rocket className="h-3 w-3" /> {busy ? 'Generating…' : accounts.length ? 'Regenerate All 9' : 'Generate All 9'}
        </button>
      </div>
      {accounts.length === 0 ? (
        <p className="text-xs text-zinc-500" data-testid="cleaners-demo-empty">
          No demo accounts yet — tap Generate to build all 9 fully populated, self-contained accounts.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {accounts.map((a) => (
            <Link key={a.property_type} to={`/cleaners-demo/${a.property_type}`}
                  className="rounded-lg p-3 transition hover:opacity-90 block"
                  style={{ backgroundColor: '#0f0f0f', border: `1px solid ${a.accent || '#22d3ee'}66` }}
                  data-testid={`cleaners-demo-tile-${a.property_type}`}>
              <p className="text-[10px] uppercase tracking-widest font-black" style={{ color: a.accent || '#22d3ee' }}>
                {a.necs_name}
              </p>
              <p className="text-sm font-bold text-white truncate">{a.property_name}</p>
              <p className="text-[10px] text-zinc-500 flex items-center gap-1 mt-1">
                {a.city}, TX · {a.login?.email}
                <ExternalLink className="h-3 w-3 ml-auto" style={{ color: a.accent || '#22d3ee' }} />
              </p>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
};

export default CleanersDemoAccountsCard;
